import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Avatar,
  ButtonBase,
  Card,
  Grid,
  InputAdornment,
  OutlinedInput,
  Popper,
  Typography,
  Box,
  IconButton,
  List,
  Divider,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
  Paper,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { IconAdjustmentsHorizontal, IconSearch, IconX } from "@tabler/icons";
import PopupState, { bindPopper, bindToggle } from "material-ui-popup-state";
import Transitions from "../../../../ui-component/extended/Transitions";
import Fade from "@mui/material/Fade";
import axios from "axios";

const PopperStyle = styled(Popper)(({ theme }) => ({
  zIndex: 1100,
  width: "99%",
  top: "-55px !important",
  padding: "0 12px",
  [theme.breakpoints.down("sm")]: {
    padding: "0 10px",
  },
}));

const OutlineInputStyle = styled(OutlinedInput)(({ theme }) => ({
  width: 434,
  marginLeft: 16,
  paddingLeft: 16,
  paddingRight: 16,
  "& input": {
    background: "transparent !important",
    paddingLeft: "4px !important",
  },
  [theme.breakpoints.down("lg")]: {
    width: 250,
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    marginLeft: 4,
    background: "#fff",
  },
}));

const HeaderAvatarStyle = styled(Avatar)(({ theme }) => ({
  ...theme.typography.commonAvatar,
  ...theme.typography.mediumAvatar,
  background: theme.palette.secondary.light,
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
}));

const MobileSearch = ({ value, setValue, popupState }) => {
  const theme = useTheme();

  return (
    <OutlineInputStyle
      id="input-search-header"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      placeholder="Search"
      startAdornment={
        <InputAdornment position="start">
          <IconSearch stroke={1.5} size="1rem" color={theme.palette.grey[500]} />
        </InputAdornment>
      }
      endAdornment={
        <InputAdornment position="end">
          <ButtonBase sx={{ borderRadius: "12px" }}>
            <HeaderAvatarStyle variant="rounded">
              <IconAdjustmentsHorizontal stroke={1.5} size="1.3rem" />
            </HeaderAvatarStyle>
          </ButtonBase>
          <Box sx={{ ml: 2 }}>
            <ButtonBase sx={{ borderRadius: "12px" }}>
              <Avatar
                variant="rounded"
                sx={{
                  ...theme.typography.commonAvatar,
                  ...theme.typography.mediumAvatar,
                  background: theme.palette.orange.light,
                  color: theme.palette.orange.dark,
                  "&:hover": {
                    background: theme.palette.orange.dark,
                    color: theme.palette.orange.light,
                  },
                }}
                {...bindToggle(popupState)}
              >
                <IconX stroke={1.5} size="1.3rem" />
              </Avatar>
            </ButtonBase>
          </Box>
        </InputAdornment>
      }
      aria-describedby="search-helper-text"
      inputProps={{ "aria-label": "weight" }}
    />
  );
};

MobileSearch.propTypes = {
  value: PropTypes.string,
  setValue: PropTypes.func,
  popupState: PropTypes.object,
};

const SearchSection = () => {
  const theme = useTheme();
  const [value, setValue] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState();
  const [filteredItems, setFilteredItems] = useState([]);
  const navigate = useNavigate();
  const searchRef = useRef(null);

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (value.trim() === "") {
        setFilteredItems([]);
        setOpen(false);
        return;
      }

      try {
        const url = `https://dev.api.farmershoptechindia.com/admin/search?filterBy=3&searchQuery=${value}`;
        console.log(`Fetching data from: ${url}`); // Log URL for debugging
        const response = await axios.get(url, {
          headers: {
            token: localStorage.getItem("token"),
          },
        });

        console.log('API response:', response.data); // Log full response
        if (response.data && response.data.data) {
          setFilteredItems(response.data.data);
          setOpen(true);
        } else {
          setFilteredItems([]);
          setOpen(false);
          console.log('No data found or unexpected response structure');
        }
      } catch (error) {
        console.error("Error fetching search results:", error);
        setFilteredItems([]);
        setOpen(false);
      }
    };

    fetchSearchResults();
  }, [value]);


  const handleChange = (event) => {
    const inputValue = event.target.value;
    setValue(inputValue);
    if (inputValue.trim() !== "") {
      setAnchorEl(event.currentTarget);
      setOpen(true);
      setPlacement("bottom");
    } else {
      setOpen(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      navigate("/main2/details", { state: { value } });
      setOpen(false);
      setValue("");
    }
  };

  const handleItemClick = (item) => {
    navigate(item.link);
  };

  return (
    <>
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <PopupState variant="popper" popupId="demo-popup-popper">
          {(popupState) => (
            <>
              <Box sx={{ ml: 2 }}>
                <ButtonBase sx={{ borderRadius: "12px" }}>
                  <HeaderAvatarStyle variant="rounded" {...bindToggle(popupState)}>
                    <IconSearch stroke={1.5} size="1.2rem" />
                  </HeaderAvatarStyle>
                </ButtonBase>
              </Box>
              <PopperStyle {...bindPopper(popupState)} transition>
                {({ TransitionProps }) => (
                  <Transitions type="zoom" {...TransitionProps} sx={{ transformOrigin: "center left" }}>
                    <Card
                      sx={{
                        background: "#fff",
                        [theme.breakpoints.down("sm")]: {
                          border: 0,
                          boxShadow: "none",
                        },
                      }}
                    >
                      <Box sx={{ p: 2 }}>
                        <Grid container alignItems="center" justifyContent="space-between">
                          <Grid item xs>
                            <MobileSearch value={value} setValue={setValue} popupState={popupState} />
                          </Grid>
                        </Grid>
                      </Box>
                    </Card>
                  </Transitions>
                )}
              </PopperStyle>
            </>
          )}
        </PopupState>
      </Box>
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <OutlineInputStyle
          id="input-search-header"
          value={value}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          placeholder="Search"
          ref={searchRef}
          startAdornment={
            <InputAdornment position="start">
              <IconSearch stroke={1.5} size="1rem" color={theme.palette.grey[500]} />
            </InputAdornment>
          }
          endAdornment={
            <InputAdornment position="end">
              <ButtonBase sx={{ borderRadius: "12px" }}>
                <HeaderAvatarStyle variant="rounded">
                  <IconAdjustmentsHorizontal stroke={1.5} size="1.3rem" />
                </HeaderAvatarStyle>
              </ButtonBase>
            </InputAdornment>
          }
          aria-describedby="search-helper-text"
          inputProps={{
            "aria-label": "weight",
            autocomplete: "new-password",
            form: {
              autocomplete: "off",
            },
          }}
        />
      </Box>
      <Popper
        sx={{
          zIndex: 999999,
          position: "absolute",
          top: "100%",
          left: "500px",
          transform: "translateX(-50%)",
          width: 460,
        }}
        open={open}
        anchorEl={searchRef.current}
        placement={placement}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper>
              <List>
                {filteredItems.length === 0 ? (
                  <Typography variant="body1" align="center">
                    No items found
                  </Typography>
                ) : (
                  filteredItems.slice(0, 5).map((item, index) => (
                    <React.Fragment key={index}>
                      <ListItemButton onClick={() => handleItemClick(item)}>
                        <ListItemAvatar>
                          <Avatar src={item.avatar} alt={item.primary} />
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <div>
                              <Typography variant="subtitle1" color="textSecondary">
                                {item.name}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {item.email}
                              </Typography>
                            </div>
                          }
                          secondary={
                            <Typography variant="body2" color="textSecondary">
                              {item.mobile}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                      {index !== 4 && <Divider />}
                    </React.Fragment>
                  ))
                )}
              </List>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};

const highlightMatch = (text = "", query = "") => {
  if (!text) return <span>{text}</span>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return (
    <span>
      {parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <Typography
            key={index}
            component="span"
            variant="subtitle1"
            color="primary"
          >
            {part}
          </Typography>
        ) : (
          part
        )
      )}
    </span>
  );
};

export default SearchSection;